import type { PermissionBag } from '@utilities-one/gpm-ui';

import { deepMerge, isPlainObject } from '@/library/lang/objects';

/**
 * The minimal user shape the merge needs — both the `/users/me` payload
 * (`AccessUser`) and `getUserDetails` (`UserDetails`) satisfy it.
 */
export interface PermissionsHolder {
  permissions?: unknown;
  role?: { permissions?: unknown; strict?: boolean } | null;
}

// Memoised per user object: React Query's structural sharing keeps `me` / details identity stable until the payload changes.
const merged = new WeakMap<object, PermissionBag | null>();

/**
 * The user's effective permissions, and the bag the permission editors start
 * from (UG-5193). Same rule as the API's `mergePermissions`: a strict role
 * ignores personal overrides, so the UI must not show them as live either —
 * otherwise the editor would display grants the API never applies. Returns
 * the role bag when either side is empty.
 */
export const mergePermissions = (user: PermissionsHolder): PermissionBag | null => {
  const cached = merged.get(user);
  if (cached !== undefined) return cached;

  const rolePermissions = isPlainObject(user.role?.permissions) ? user.role.permissions : null;
  const userPermissions = isPlainObject(user.permissions) ? user.permissions : null;
  // A role with no bag still honours the override (same rule as the API's mergePermissions).
  const result =
    !userPermissions || user.role?.strict ? rolePermissions : deepMerge(rolePermissions ?? {}, userPermissions);

  merged.set(user, result);
  return result;
};
